/**
 * Live Share container join (BUILD-SPEC.md §5).
 *
 * Joins (or creates) the meeting's Live Share container and initializes the
 * three shared Fluid objects this app uses: a `LiveTimer` for the shared
 * countdown, a `LiveState<Session>` for the rest of the standup's state, and
 * a `LivePresence` for the cosmetic "who has this open" marker.
 *
 * This file only wires the join — the `SessionController` implementation
 * (event guards, discrete-write policy) lives in `session.ts`; the
 * `finished`/zero-point handling lives in `timer.ts`.
 *
 * BUILD-SPEC hard rule 5 ("no role gating"): none of `initialize()` calls
 * below pass `allowedRoles` — everyone with the app open gets every
 * control, deliberately.
 */
import { LiveShareClient, LiveTimer, LiveState, LivePresence } from "@microsoft/live-share";
import { LiveShareHost } from "@microsoft/teams-js";
import type { Session } from "../engine";
import { TICK_RATE_MS } from "./timer";

/**
 * The three shared objects, keyed exactly as declared to `joinContainer` so
 * they come back out the same way from `container.initialObjects`.
 */
interface InitialObjects {
  timer: LiveTimer;
  session: LiveState<Session>;
  presence: LivePresence;
}

export interface JoinedLiveShare {
  timer: LiveTimer;
  session: LiveState<Session>;
  presence: LivePresence;
  /**
   * The ONLY clock allowed for anything shared (BUILD-SPEC hard rule 3).
   * Backed by `timestampProvider.getTimestamp()` from `joinContainer` —
   * never `Date.now()`.
   *
   * Returns SECONDS, not milliseconds. `timestampProvider.getTimestamp()`
   * itself returns milliseconds — this wrapper divides by 1000 right here,
   * at the one place a raw Live Share timestamp enters the app, because
   * `src/engine` adds `now - turnStartedAt` directly onto `spentSeconds`
   * (documented in seconds) with no conversion of its own. Getting this
   * wrong here does not fail loudly: it makes overtime explode almost
   * instantly, because every real millisecond of wall-clock time is
   * miscounted as a full second of elapsed speaking time.
   */
  now: () => number;
}

/**
 * Joins the meeting's Live Share container and initializes the three shared
 * objects. Throws if Live Share is unavailable (no host, tenant disabled,
 * network failure, etc.) — the caller (`session.ts`'s `ControllerFactory`)
 * is responsible for catching that and falling back to
 * `src/local/localTimer.ts`. This function itself never falls back; keeping
 * that decision one level up keeps this file a straight, honest wrapper
 * around the SDK calls documented in BUILD-SPEC §5.
 */
export async function joinLiveShare(initialSession: Session): Promise<JoinedLiveShare> {
  const client = new LiveShareClient(LiveShareHost.create());

  const { container, timestampProvider } = await client.joinContainer({
    initialObjects: { timer: LiveTimer, session: LiveState, presence: LivePresence },
  });

  const { timer, session, presence } = container.initialObjects as unknown as InitialObjects;

  // No allowedRoles anywhere below — see file doc.
  await timer.initialize();
  timer.tickRate = TICK_RATE_MS;

  await session.initialize(initialSession);
  await presence.initialize();

  return {
    timer,
    session,
    presence,
    now: () => timestampProvider.getTimestamp() / 1000,
  };
}
